import {
  ApplicationRef,
  Injectable,
  afterNextRender,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class FontResizeService {
  fontStep = 0;
  private readonly maxStep = 3;
  private readonly minStep = -3;
  private readonly storageKey = 'rise_fontStep';

  isSpeaking = false;
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private applyTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly appRef = inject(ApplicationRef);
  private readonly router = inject(Router);

  constructor() {
    const saved = localStorage.getItem(this.storageKey);
    if (saved !== null && saved !== '') {
      this.fontStep = Number(saved);
    }

    afterNextRender(() => this.scheduleApply());

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.scheduleApply());

    this.appRef.isStable.subscribe((stable) => {
      if (stable) {
        this.scheduleApply();
      }
    });
  }

  increase() {
    if (this.fontStep >= this.maxStep) {
      return;
    }
    this.fontStep++;
    localStorage.setItem(this.storageKey, this.fontStep.toString());
    this.applyFontSize();
  }

  decrease() {
    if (this.fontStep <= this.minStep) {
      return;
    }
    this.fontStep--;
    localStorage.setItem(this.storageKey, this.fontStep.toString());
    this.applyFontSize();
  }

  reset() {
    this.fontStep = 0;
    localStorage.setItem(this.storageKey, '0');
    this.clearAppliedFontSizes();
  }

  /** Debounced apply so refresh / route changes wait for DOM paint. */
  private scheduleApply() {
    if (this.fontStep === 0) {
      return;
    }

    if (this.applyTimer) {
      clearTimeout(this.applyTimer);
    }

    this.applyTimer = setTimeout(() => {
      requestAnimationFrame(() => this.applyFontSize());
    }, 200);
  }

  private getTargetElements(): HTMLElement[] {
    const root = document.querySelector('app-root');
    if (!root) {
      return [];
    }

    const skipTags = new Set(['SVG', 'I', 'PATH', 'IMG', 'CANVAS']);
    const skipSelector =
      'svg, i, .bi, .accessibility-fab, .accessibility-panel, highcharts-chart, .font-slider, .theme-dot, .dot, .slider-dot';

    return Array.from(root.querySelectorAll<HTMLElement>('*')).filter((el) => {
      if (skipTags.has(el.tagName)) {
        return false;
      }
      if (el.matches(skipSelector)) {
        return false;
      }
      if (el.closest('.accessibility-panel, .accessibility-fab')) {
        return false;
      }
      return true;
    });
  }

  private applyFontSize() {
    const elements = this.getTargetElements();
    if (!elements.length) {
      return;
    }

    // Pass 1: capture true base sizes before any inline changes (prevents compounding on nested elements).
    elements.forEach((el) => {
      if (!el.dataset['originalFontSize']) {
        const base = parseFloat(getComputedStyle(el).fontSize);
        if (!Number.isNaN(base) && base > 0) {
          el.dataset['originalFontSize'] = base.toString();
        }
      }
    });

    // Pass 2: apply step from stored originals only.
    elements.forEach((el) => {
      const stored = el.dataset['originalFontSize'];
      if (!stored) {
        return;
      }

      const baseSize = parseFloat(stored);
      if (Number.isNaN(baseSize)) {
        return;
      }

      if (this.fontStep === 0) {
        el.style.fontSize = '';
        el.removeAttribute('data-original-font-size');
        return;
      }

      el.style.setProperty(
        'font-size',
        `${baseSize + this.fontStep}px`,
        'important'
      );
    });
  }

  private clearAppliedFontSizes() {
    document.querySelectorAll('[data-original-font-size]').forEach((node) => {
      const el = node as HTMLElement;
      el.style.fontSize = '';
      el.removeAttribute('data-original-font-size');
    });
  }

  speakText(text: string) {
    if (!text || text.trim().length < 2) {
      return;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text.trim());
    utterance.lang = 'en-IN';
    utterance.rate = 0.95;

    window.speechSynthesis.speak(utterance);
  }

  private onMouseOver = (e: MouseEvent) => {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }

    const el = e.target as HTMLElement;

    const text =
      el.innerText ||
      el.getAttribute('aria-label') ||
      el.getAttribute('alt') ||
      '';

    if (!text.trim()) {
      return;
    }

    this.hoverTimer = setTimeout(() => {
      this.speakText(text);
    }, 400);
  };

  private onMouseOut = () => {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }
  };

  private onMouseUp = () => {
    const selected = window.getSelection()?.toString();

    if (selected && selected.trim().length > 1) {
      this.speakText(selected);
    }
  };

  startListeners() {
    document.addEventListener('mouseover', this.onMouseOver);
    document.addEventListener('mouseout', this.onMouseOut);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  stopListeners() {
    document.removeEventListener('mouseover', this.onMouseOver);
    document.removeEventListener('mouseout', this.onMouseOut);
    document.removeEventListener('mouseup', this.onMouseUp);

    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }
  }

  toggleScreenReader() {
    if (this.isSpeaking) {
      window.speechSynthesis.cancel();
      this.isSpeaking = false;
      this.stopListeners();
      return;
    }

    this.isSpeaking = true;
    this.startListeners();

    this.speakText(
      'Screen reader enabled. Hover over or select any text to hear it read aloud.'
    );
  }

  stopSpeech() {
    window.speechSynthesis.cancel();
    this.isSpeaking = false;
    this.stopListeners();
  }
}
